import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Table } from 'antd';
import { Container } from 'react-bootstrap';
import { toast, ToastContainer } from 'react-toastify';
import { FaMoon } from 'react-icons/fa';
import { BreathAPIs } from '../../APIs/BreathAPIs';
import styles from './CustomerBookingStyles.module.css';

// Internal filter: customers only see active bookings. The API matches any status in the list.
const BOOKING_STATUSES = ['Inprogress', 'Idle', 'Upcoming'];

const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleTimeString(undefined, {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        hour12: true
    });
};

const convertSecondsToHoursAndMinutes = (totalSeconds = 0) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    return `${hours} h ${minutes} m`;
};

const CustomerBookingView = ({ customerLocation }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [bookings, setBookings] = useState([]);
    const [aggregatedBookingStats, setAggregatedBookingStats] = useState({});
    const [pagination, setPagination] = useState({
        total: 0,
        current: 1,
        pageSize: 10,
        totalPages: 0
    });

    const getBookings = useCallback(async (page = 1, pageSize = 10) => {
        const locationIds = Array.isArray(customerLocation) ? customerLocation : [];

        if (locationIds.length === 0) {
            setBookings([]);
            setAggregatedBookingStats({});
            setPagination({ total: 0, current: 1, pageSize, totalPages: 0 });
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        try {
            const res = await BreathAPIs.getBookingsList({
                page,
                limit: pageSize,
                filterLocation: locationIds,
                bookingStatus: BOOKING_STATUSES
            });
            setBookings(res.data.bookings || []);
            setAggregatedBookingStats(res.data.aggregatedBookingStats || {});
            setPagination({
                total: res.data.pagination.total,
                current: res.data.pagination.current,
                pageSize: res.data.pagination.pageSize,
                totalPages: res.data.pagination.totalPages
            });
        } catch (error) {
            console.error(error);
            toast.error('Error fetching bookings.');
        } finally {
            setIsLoading(false);
        }
    }, [customerLocation]);

    useEffect(() => {
        getBookings();
    }, [getBookings]);

    const handleTableChange = ({ current, pageSize }) => {
        getBookings(current, pageSize);
    };

    const columnBookings = [
        {
            title: 'User',
            dataIndex: 'name',
            key: 'name',
            render: (text, record) => (
                <span> {record?.user?.name == null ? `${record?.user?.firstName ?? ''} ${record?.user?.lastName ?? ''}` : record.user.name} (<i>{record?.user?.email}</i>)</span>
            ),
            width: 250
        },
       
        {
            title: 'Status',
            dataIndex: 'bookingStatus',
            key: 'bookingStatus',
            width: 120,
        },
        {
            title: 'Contact',
            dataIndex: 'phone',
            key: 'phone',
            width: 230,
            render: (text, record) => (
                <span> {record?.user?.phone ? record.user.phone : 'Not Available'} </span>
            )
        },
        {
            title: 'Start Time',
            dataIndex: 'startTime',
            key: 'startTime',
            render: (text, record) => (
                <span className={(new Date(record.startTime) < new Date()) ? styles['past-date'] : ''}> {formatDate(record.startTime)}</span>
            ),
            width: 200
        },
        {
            title: 'End Time',
            dataIndex: 'endTime',
            key: 'endTime',
            render: (text, record) => {
                const endTime = record.isNightCharging ? record.endTimeOverNight : record.endTime;
                return (
                    <span className={(new Date(endTime) < new Date()) ? styles['past-date'] : ''}> {formatDate(endTime)}</span>
                );
            },
            width: 200
        },
        {
            title: 'Scheduled End Time',
            dataIndex: 'endTimeScheduled',
            key: 'endTimeScheduled',
            render: (text, record) => (
                <span className={(new Date(record.endTimeScheduled) < new Date()) ? styles['past-date'] : ''}> {formatDate(record.endTimeScheduled)}</span>
            ),
            width: 200
        },
        {
            title: 'Charger',
            dataIndex: 'charger',
            key: 'charger',
            render: (text, record) => (
                <span> {record?.charger?.chargerTag} ({record?.sessions?.length ?? 0} sessions) <br /> <i>{record?.charger?.location?.locationName}</i></span>
            ),
            width: 250
        },
    ];

    return (
        <Container>
            <ToastContainer></ToastContainer>

            <div className={styles['page-header']}>
                <h5>Active Bookings</h5>
            </div>

           

            <Table
                loading={isLoading}
                dataSource={bookings}
                columns={columnBookings}
                rowKey="_id"
                pagination={pagination}
                onChange={handleTableChange}
                className={styles['data-list']}
                bordered
                size="middle"
                scroll={{ x: 'calc(600px + 50%)' }}
            />
        </Container>
    );
};

CustomerBookingView.propTypes = {
    customerLocation: PropTypes.arrayOf(
        PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    ),
};

const mapStateToProps = (state) => ({
    customerLocation: state.auth?.user?.customerLocation ?? null,
});

export default connect(mapStateToProps)(CustomerBookingView);
